<template>
  <v-container class="pa-0 fill-height" fluid>
    <!-- Snackbar para operaciones -->
    <v-snackbar v-model="snackbar" :timeout="timeout" :color="snackbarColor">{{
      snackbarText
    }}</v-snackbar>

    <!-- Layout de carga -->
    <LoadingLayout v-if="isLoading">
      <CircularLoading>
        Registrando producto
      </CircularLoading>
    </LoadingLayout>

    <!-- Contenido principal -->
    <v-container v-else fluid>
      <!-- Fila para el texto y los botones -->
      <DeltiaCard class="pb-5" v-if="!isLoading">
        <!-- Columna para el título -->
        <v-col cols="12" md="9" class="mb-5 mb-md-0">
          <p class="text-h5 font-weight-bold mb-0 pb-0">Nuevo producto</p>
        </v-col>
        <!-- Columna para guardar el producto -->
        <v-col cols="12" md="3">
          <PrimaryButton @click="createProduct">Guardar</PrimaryButton>
        </v-col>
      </DeltiaCard>
      <!-- Fila para los datos principales y la imagen -->
      <DeltiaCard class="pb-6" v-if="!isLoading">
        <v-row no-gutters>
          <!-- Datos principales -->
          <v-col cols="12" md="9">
            <!-- Nombre y descripción -->
            <v-row>
              <!-- Nombre -->
              <v-col class="pt-0 pb-0" cols="12">
                <!-- Ayuda -->
                <p class="mb-1 font-weight-medium text--disabled">Nombre</p>
                <!-- Campo de texto -->
                <TextField
                  class="margin-no"
                  placeholder="Agregar nombre"
                  v-model="name"
                />
              </v-col>
              <!-- Descripción -->
              <v-col class="mt-3 pt-0 pb-0" cols="12">
                <!-- Ayuda -->
                <p class="mb-1 font-weight-medium text--disabled">
                  Descripción
                </p>
                <!-- Campo de texto -->
                <TextField
                  class="margin-no"
                  placeholder="Agregar descripción"
                  v-model="description"
                />
              </v-col>
            </v-row>
            <!-- Precio de venta y de producción -->
            <v-row class="mt-3">
              <!-- Costo de producción -->
              <v-col class="pt-0 pb-0" cols="12" md="6">
                <!-- Ayuda -->
                <p class="mb-1 font-weight-medium text--disabled">
                  Costo de producción
                </p>
                <!-- Campo de texto -->
                <TextField
                  type="number"
                  class="margin-no"
                  placeholder="0.0"
                  v-model="production"
                />
              </v-col>
              <!-- Precio de venta -->
              <v-col class="pt-0 pb-0" cols="12" md="6">
                <!-- Ayuda -->
                <p class="mb-1 font-weight-medium text--disabled">
                  Precio de venta
                </p>
                <!-- Campo de texto -->
                <TextField
                  type="number"
                  class="margin-no"
                  placeholder="0.0"
                  v-model="price"
                />
                <!-- Utilidad -->
                <p
                  class="mt-2 font-weight-medium blue-grey--text text--darken-3"
                >
                  Utilidad: ${{ utility }}
                </p>
              </v-col>
            </v-row>
          </v-col>
          <!-- Imagen del producto -->
          <v-col cols="12" md="3" align="right" class="pl-sm-2">
            <v-img>
              <picture-input
                ref="pictureInput"
                width="600"
                height="600"
                margin="5"
                accept="image/jpeg, image/png"
                size="10"
                class="picture-input"
                button-class="btn"
                :hideChangeButton="true"
                :custom-strings="{
                  upload: '<h1>Oops!</h1>',
                  drag:
                    'Arrastra una imagen o <br>da click para seleccionar una',
                  tap: 'Toca para seleccionar una imagen <br>de tu galería',
                  change: 'Cambiar imagen',
                  remove: 'Remover imagen'
                }"
                @change="onImageSelected"
              />
            </v-img>
          </v-col>
        </v-row>
      </DeltiaCard>
      <!-- Fila para la sección de sucursales -->
      <DeltiaCard class="pb-6" v-if="!isLoading">
        <!-- Columna para el título -->
        <v-col cols="12">
          <p class="text-h5 font-weight-medium mb-0 pb-0">Sucursales</p>
        </v-col>
        <!-- Columna para activar el inventario -->
        <v-col v-if="activeBranches.length > 0" cols="12">
          <v-checkbox
            color="secondary"
            v-model="isStockAvailable"
            label="Activar control de inventario"
          />
        </v-col>
        <!-- Columna de texto para sucursales activas -->
        <v-col v-if="activeBranches.length > 0" cols="12">
          <p class="mb-1 font-weight-medium text--disabled">Disponible en</p>
          <v-divider></v-divider>
        </v-col>
        <!-- Columna para las sucursales activas -->
        <v-col cols="12">
          <!-- Fila para cada sucursal -->
          <v-row
            v-for="branch in activeBranches"
            :key="branch._id"
            class="mt-3 mb-3"
            no-gutters
          >
            <!-- Nombre de la sucursal -->
            <v-col :cols="isStockAvailable ? '3' : '11'">
              <!-- Nombre de la sucursal -->
              <p class="text-overline">{{ branch.name }}</p>
            </v-col>
            <!-- Cantidad disponible (si el inventario está activado) -->
            <v-col cols="8" v-if="isStockAvailable">
              <!-- Campo de texto -->
              <TextField
                placeholder="Cantidad disponible"
                v-model="branch.quantity"
              />
            </v-col>
            <!-- Quitar sucursal -->
            <v-col cols="1" align="center">
              <v-btn
                @click="setInactiveBranch(branch)"
                color="red darken-3"
                icon
              >
                <v-icon>mdi-minus</v-icon>
              </v-btn>
            </v-col>
            <!-- Línea divisora -->
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
          </v-row>
        </v-col>
        <!-- Columna de texto para sucursales inactivas -->
        <v-col v-if="inactiveBranches.length > 0" cols="12">
          <p class="mb-1 font-weight-medium text--disabled">No disponible en</p>
          <v-divider></v-divider>
        </v-col>
        <!-- Columna para las sucursales inactivas -->
        <v-col v-if="inactiveBranches.length > 0" cols="12">
          <!-- Fila para cada sucursal -->
          <v-row
            v-for="branch in inactiveBranches"
            :key="branch._id"
            class="mt-3 mb-3"
            no-gutters
          >
            <!-- Nombre de la sucursal -->
            <v-col cols="11">
              <!-- Nombre de la sucursal -->
              <p class="text-overline">{{ branch.name }}</p>
            </v-col>
            <!-- Agregar sucursal -->
            <v-col cols="1" align="center">
              <v-btn
                @click="setActiveBranch(branch)"
                color="teal darken-3"
                icon
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-col>
            <!-- Línea divisora -->
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
          </v-row>
        </v-col>
      </DeltiaCard>
    </v-container>
  </v-container>
</template>

<script>
// importar componentes
import LoadingLayout from "@/layouts/LoadingLayout";
import CircularLoading from "@/components/Loading/Circular";
import DeltiaCard from "@/components/DeltiaCard";
import TextField from "@/components/TextField";
import PrimaryButton from "@/components/PrimaryButton";

// Importar dependencias
import { mapGetters } from "vuex";
import PictureInput from "vue-picture-input";
import BranchesServices from "@/services/BranchesServices";

// Importar servicios
import ProductsServices from "@/services/ProductsServices";
import StocksServices from "@/services/StocksServices";

export default {
  components: {
    LoadingLayout,
    CircularLoading,
    TextField,
    DeltiaCard,
    PrimaryButton,
    PictureInput
  },
  data() {
    return {
      name: "",
      price: "",
      timeout: 4000,
      snackbar: false,
      isLoading: false,
      production: "",
      description: "",
      snackbarText: "",
      snackbarColor: "",
      isStockAvailable: false,
      activeBranches: [],
      inactiveBranches: []
    };
  },
  created: async function() {
    try {
      // Obtener sucursales
      this.activeBranches = await BranchesServices.getBranchesInUserProfile();
    } catch (err) {
      // Mostrar error
      this.showError(err.message);
    }
  },
  methods: {
    setInactiveBranch(targetBranch) {
      // Remover sucursal de las sucursales activas
      this.activeBranches = this.activeBranches.filter(
        branch => branch._id !== targetBranch._id
      );

      // Agregar sucursal a las sucursales inactivas
      this.inactiveBranches.unshift(targetBranch);
    },
    setActiveBranch(targetBranch) {
      // Remover sucursal de las sucursales inactivas
      this.inactiveBranches = this.inactiveBranches.filter(
        branch => branch._id !== targetBranch._id
      );

      // Agregar sucursal a las sucursales activas
      this.activeBranches.unshift(targetBranch);
    },
    onImageSelected(image) {
      if (image) {
        // Guardar el archivo de la imagen
        this.imageFile = this.$refs.pictureInput.file;
      } else {
        // Mostrar error
        this.showError("No se pudo cargar la imagen");
      }
    },
    async createProduct() {
      // Ponemos la pantalla de carga
      this.isLoading = true;

      // Verificar que los campos estén completos
      if (!this.areFieldsComplete) {
        // Emitir evento con el error
        this.isLoading = false;
        this.showError("Rellena los campos necesarios, por favor");
        return;
      }

      // Realizamos la petición
      try {
        const productId = await ProductsServices.createProduct({
          name: this.name,
          description: this.description,
          purchase_price: this.production,
          sale_price: this.price,
          stock_control: this.isStockAvailable
        });

        // Revisamos si ha subido alguna imagen
        if (this.imageFile) {
          // Crear formulario con la imagen
          const fd = new FormData();
          fd.append("image", this.imageFile, this.imageFile.name);

          await ProductsServices.addImage(productId, fd);
        }

        // Revisamos si se agregará a alguna sucursal
        if (this.activeBranches.length > 0) {
          await StocksServices.createStocksWithQuantity(
            productId,
            this.activeBranches
          );
        }

        // Enviar evento a mixpanel
        this.$mixpanel.track("new-product", {
          distinct_id: this.getProfile.business_id,
          purchase_price: this.production,
          sale_price: this.price,
          utility: this.price - this.production
        });

        this.isLoading = false;
        // regresamos a la página de productos
        this.$router.push({ name: "products" });
      } catch (err) {
        // Desactivamos el dialogo
        this.isLoading = false;
        // Mostrar error
        this.showError(err.message);
      }
    },
    showError(errorMessage) {
      this.snackbarText = errorMessage;
      this.snackbarColor = "error";
      this.snackbar = true;
    }
  },
  computed: {
    ...mapGetters(["getProfile"]),
    utility() {
      return parseFloat(Math.round((this.price - this.production) * 100) / 100);
    },
    areFieldsComplete() {
      // Revisar que los campos necesarios estén rellenos
      if (!!this.name && !!this.description && this.price && this.production) {
        return true;
      }
      return false;
    }
  }
};
</script>

<style></style>
